import { LitElement, html, css, unsafeCSS, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { diffPanels } from './panel-diff';
import { GridStack, type GridItemHTMLElement, type GridStackNode, type GridStackOptions } from 'gridstack';
import gridstackCss from 'gridstack/dist/gridstack.css?inline';
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-panel-grid> — the ONE reusable tile CANVAS, app-wide.
 *
 * A draggable + resizable grid of panels (Gridstack), the vanilla equivalent of
 * react-grid-layout — and deliberately the SAME widget chrome the dashboard
 * uses, so the model-workspace tiles ARE reusable dashboard widgets. The CANVAS
 * only: the host (model page / dashboard) wraps it in the symbol-tree frame
 * (`.vg-wrapper` + `.vg-toolbar`) and owns the toolbar + which panels are shown.
 *
 * Each tile: a card frame · body · bottom BAR (drag-dots · title · spacer ·
 * controls incl. a trash) · a resize RING (8 edges/corners; cues fade in on
 * card-hover, blue on grab-hover, tracing the card's 8px contour). The trash
 * emits `panel-remove`; the host flips `hiddenIds` to hide/show (gridstack
 * add/remove — never reflowing the others).
 *
 * Lit owns a stable empty `.grid-stack`; items are built ONCE imperatively, each
 * projecting the host's light-DOM content through `<slot name="<id>">` — so
 * gridstack's DOM mutation never collides with Lit's render tree. gridstack.css
 * is adopted into the shadow root; the chrome reads the app CSS vars (theme).
 */

export interface PanelDef {
  id: string;
  title?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  minW?: number;
  minH?: number;
  noResize?: boolean;
  noMove?: boolean;
  /** No trash in its bar: a tile that is not on a board (a preview) has nothing to be removed from. */
  noRemove?: boolean;
}

export interface PanelLayoutItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** lucide grip-vertical — the drag-dots handle. */
const GRIP_SVG = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="5" r="1.4"/><circle cx="9" cy="12" r="1.4"/><circle cx="9" cy="19" r="1.4"/><circle cx="15" cy="5" r="1.4"/><circle cx="15" cy="12" r="1.4"/><circle cx="15" cy="19" r="1.4"/></svg>`;

@customElement('vitesse-panel-grid')
export class VitessePanelGrid extends LitElement {
  static styles = [
    unsafeCSS(gridstackCss),
    css`
      :host {
        display: block;
        min-height: 0;
      }
      .grid-stack {
        background: transparent;
      }

      /* ── card frame (the dashboard widget base). NOTE: no inset override — we
       *    let gridstack apply the margin so the card edge aligns with the
       *    resize handles (the ring traces the real contour). ── */
      .grid-stack-item-content {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: var(--card-bg, #0a0a0a);
        border: 1px solid var(--border-color, #1f1f1f);
        border-radius: var(--tile-radius);
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
      }
      .pg-body {
        flex: 1 1 auto;
        min-height: 0;
        min-width: 0;
        overflow: auto;
        position: relative;
      }
      ::slotted(*) {
        display: block;
        min-height: 100%;
      }

      /* ── bottom bar: drag-dots · title · spacer · controls(trash) ── */
      .pg-bar {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 5px 8px 5px 6px;
        min-height: var(--tile-bar-height);
        border-top: 1px solid var(--border-color, #1f1f1f);
        background: var(--bg-primary, #000); /* same as the symbol-tree toolbar */
      }
      .pg-icon-btn {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: var(--tile-control-size);
        height: var(--tile-control-size);
        border: 0;
        background: transparent;
        border-radius: 6px;
        color: var(--text-tertiary, #666);
        cursor: pointer;
        transition: color 0.12s ease, transform 0.12s ease;
      }
      .pg-icon-btn svg {
        width: var(--tile-icon-size);
        height: var(--tile-icon-size);
      }
      .pg-trash {
        color: var(--text-primary, #fff);
      }
      .pg-drag {
        cursor: grab;
      }
      .pg-drag:hover {
        color: var(--text-primary, #fff);
        transform: scale(1.07);
      }
      .pg-drag:active {
        cursor: grabbing;
      }
      .pg-trash:hover {
        color: var(--red, #e03e42);
        transform: scale(1.07);
      }
      .pg-title {
        font-size: var(--tile-title-size);
        font-weight: var(--tile-title-weight);
        color: var(--text-primary, #fff);
        white-space: nowrap;
        /* The title yields, never the controls: a tile squeezed narrow keeps its trash inside
           its own walls and clips the name instead of pushing the bin out of sight. */
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .pg-spacer {
        flex: 1 1 auto;
      }
      .pg-gesture .pg-body {
        pointer-events: none;
      }
      .pg-locked .pg-drag {
        cursor: default;
        opacity: 0.35;
      }
      .pg-locked > .ui-resizable-handle {
        display: none;
      }
      .pg-controls {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 2px;
      }
      /* The base widget's own controls (the settings gear) slot in beside the trash, same box. */
      .pg-controls ::slotted(*) {
        display: inline-flex;
        min-height: 0;
        height: var(--tile-control-size);
        align-items: center;
        justify-content: center;
        gap: 2px;
      }

      /* ── resize RING — faint cues fade in on card-hover, blue on grab-hover ──
       *    The handles sit at the card corner (margin), so cues trace the 8px
       *    contour. Small (9px corners / 18px edges). ── */
      .grid-stack-item > .ui-resizable-handle {
        background: none;
        filter: none;
      }
      .grid-stack-item > .ui-resizable-handle::after {
        content: '';
        position: absolute;
        opacity: 0;
        transition: opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
      }
      /* .pg-hover, not :hover — a tile's body is a sandbox iframe, and while
       * the pointer is inside it the parent document's :hover chain drops.
       * mouseenter/mouseleave on the item still fire (buildItem sets the class). */
      .grid-stack-item.pg-hover > .ui-resizable-handle::after {
        opacity: 0.75;
      }
      .grid-stack-item > .ui-resizable-handle:hover::after {
        opacity: 1;
      }
      /* edges — a short centred bar */
      .grid-stack-item > .ui-resizable-n::after,
      .grid-stack-item > .ui-resizable-s::after,
      .grid-stack-item > .ui-resizable-e::after,
      .grid-stack-item > .ui-resizable-w::after {
        background: var(--text-secondary, #888);
        border-radius: 2px;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
      .grid-stack-item > .ui-resizable-n::after,
      .grid-stack-item > .ui-resizable-s::after {
        width: 18px;
        height: 3px;
      }
      .grid-stack-item > .ui-resizable-e::after,
      .grid-stack-item > .ui-resizable-w::after {
        width: 3px;
        height: 18px;
      }
      .grid-stack-item > .ui-resizable-n:hover::after,
      .grid-stack-item > .ui-resizable-s:hover::after,
      .grid-stack-item > .ui-resizable-e:hover::after,
      .grid-stack-item > .ui-resizable-w:hover::after {
        background: var(--blue, #1a8cd8);
      }
      /* corners — a rounded right-angle that hugs the card's 8px corner.
       *    GRIDSTACK ROTATES THE CORNER HANDLES 45° (rotate(±45deg)); our cue is
       *    the handle's ::after, so without undoing that it renders diagonally
       *    (a chevron). transform:none re-aligns it so the L-bracket traces the
       *    contour. The un-rotated handle's outer corner sits ON the card
       *    corner, so a 3px inset + 6px radius follows the rounded edge. */
      .grid-stack-item > .ui-resizable-nw,
      .grid-stack-item > .ui-resizable-ne,
      .grid-stack-item > .ui-resizable-se,
      .grid-stack-item > .ui-resizable-sw {
        transform: none !important;
      }
      .grid-stack-item > .ui-resizable-nw::after,
      .grid-stack-item > .ui-resizable-ne::after,
      .grid-stack-item > .ui-resizable-se::after,
      .grid-stack-item > .ui-resizable-sw::after {
        width: 11px;
        height: 11px;
        border: 2px solid var(--text-secondary, #888);
      }
      .grid-stack-item > .ui-resizable-nw::after {
        top: 3px;
        left: 3px;
        border-right: 0;
        border-bottom: 0;
        border-top-left-radius: 6px;
      }
      .grid-stack-item > .ui-resizable-ne::after {
        top: 3px;
        right: 3px;
        border-left: 0;
        border-bottom: 0;
        border-top-right-radius: 6px;
      }
      .grid-stack-item > .ui-resizable-se::after {
        bottom: 3px;
        right: 3px;
        border-left: 0;
        border-top: 0;
        border-bottom-right-radius: 6px;
      }
      .grid-stack-item > .ui-resizable-sw::after {
        bottom: 3px;
        left: 3px;
        border-right: 0;
        border-top: 0;
        border-bottom-left-radius: 6px;
      }
      .grid-stack-item > .ui-resizable-nw:hover::after,
      .grid-stack-item > .ui-resizable-ne:hover::after,
      .grid-stack-item > .ui-resizable-se:hover::after,
      .grid-stack-item > .ui-resizable-sw:hover::after {
        border-color: var(--blue, #1a8cd8);
      }
      /* while a handle is HELD the whole ring lights up, not only the cue under
       * the cursor — the hand drifts off a 9px corner long before the drag ends */
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-handle::after {
        opacity: 1;
      }
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-n::after,
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-s::after,
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-e::after,
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-w::after {
        background: var(--blue, #1a8cd8);
      }
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-nw::after,
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-ne::after,
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-se::after,
      .grid-stack-item.ui-resizable-resizing > .ui-resizable-sw::after {
        border-color: var(--blue, #1a8cd8);
      }

      /* drag/resize PREVIEW — rounded like the widget, animated (not snapped) */
      .grid-stack-placeholder > .placeholder-content {
        inset: 0;
        border-radius: var(--tile-radius);
        border: 1px dashed var(--blue, #1a8cd8);
        background: color-mix(in srgb, var(--blue, #1a8cd8) 10%, transparent);
      }

      .grid-stack > .grid-stack-item.ui-draggable-dragging .grid-stack-item-content,
      .grid-stack > .grid-stack-item.ui-resizable-resizing .grid-stack-item-content {
        border-color: var(--blue, #1a8cd8);
        box-shadow: 0 0 0 1px var(--blue, #1a8cd8);
      }
      .grid-stack-item.ui-draggable-dragging,
      .grid-stack-item.ui-resizable-resizing {
        opacity: 0.94;
        z-index: 5;
      }
    `,
  ];

  @property({ attribute: false }) panels: PanelDef[] = [];
  /** Persisted placements (override the panel defaults). Keyed by panel id. */
  @property({ attribute: false }) layout?: PanelLayoutItem[];
  /** Panel ids to HIDE — the host owns this (toolbox toggle / trash). */
  @property({ attribute: false }) hiddenIds: string[] = [];
  @property({ type: Number }) column = 12;
  @property({ type: Number }) cellHeight = 72;
  @property({ type: Boolean }) float = true;
  /** A viewer's grid: nothing moves, nothing resizes, no bin. A shared board. */
  @property({ type: Boolean }) locked = false;

  private grid?: GridStack;
  private readonly itemEls = new Map<string, HTMLElement>();
  private readonly shown = new Set<string>();

  render() {
    // Lit owns ONLY this stable container; items are built imperatively below.
    return html`<div class="grid-stack"></div>`;
  }

  protected firstUpdated(): void {
    const gridEl = this.renderRoot.querySelector('.grid-stack') as HTMLElement | null;
    if (!gridEl) return;

    const placed = new Map((this.layout ?? []).map((l) => [l.id, l]));
    for (const p of this.panels) {
      const el = this.buildItem(p, placed.get(p.id));
      this.itemEls.set(p.id, el);
      this.shown.add(p.id);
      gridEl.appendChild(el);
    }

    const opts: GridStackOptions = {
      column: this.column,
      cellHeight: this.cellHeight,
      margin: 8,
      float: this.float, // keep placements (no auto-compact) → less layout shift
      animate: true, // ease moves/resizes + the placeholder, not snapping
      handle: '.pg-drag',
      resizable: { handles: 'n,e,s,w,ne,nw,se,sw' },
      // gridstack's own auto-hide toggles the handles' display on mouseenter,
      // which a tile whose body is an iframe does not reliably raise. The
      // handles stay in the DOM; the ring below fades their CUES in on hover.
      alwaysShowResizeHandle: true,
      acceptWidgets: false,
      draggable: { scroll: false }, // we drive an exponential PAGE scroll ourselves
    };
    this.grid = GridStack.init(opts, gridEl);
    this.grid.on('change', () => this.scheduleEmit());

    // gridstack keeps only ONE handler per drag/resize event (it overwrites, it
    // does NOT add), so each event's work goes in a single callback.
    // (anchor) While MOVING a tile, lock every OTHER tile so a placed tile is
    // never pushed/displaced — the dragged tile settles into free space instead.
    // RESIZE deliberately does NOT lock: growing a tile smartly pushes neighbors
    // out of the way (gridstack default), which is wanted for resize.
    // (auto-scroll) Dragging/resizing toward the scroll-container edge scrolls
    // the page, accelerating with how far past the edge the cursor is.
    // (iframes) A tile whose body is an iframe swallows the pointer the moment a drag or resize
    // crosses it, and the gesture stutters or drops. While a gesture is live, no tile body takes
    // the pointer — the grid does.
    this.grid.on('dragstart', (e, el) => {
      this.lockOthers(el as GridItemHTMLElement);
      gridEl.classList.add('pg-gesture');
      this.startAutoScroll(e);
    });
    this.grid.on('dragstop', () => {
      this.unlockAll();
      gridEl.classList.remove('pg-gesture');
      this.stopAutoScroll();
    });
    this.grid.on('resizestart', (e) => {
      gridEl.classList.add('pg-gesture');
      this.startAutoScroll(e);
    });
    this.grid.on('resizestop', () => {
      gridEl.classList.remove('pg-gesture');
      this.stopAutoScroll();
    });

    // A slotted tile whose body is an iframe reports its own enter/leave (see
    // widget-host 'hover'); the item wrapping that slot lights its ring from it.
    this.addEventListener('tile-hover', (e: Event) => {
      const id = (e.target as HTMLElement | null)?.getAttribute('slot') ?? '';
      this.itemEls.get(id)?.classList.toggle('pg-hover', Boolean((e as CustomEvent<boolean>).detail));
    });

    // Apply any initial hidden set.
    if (this.hiddenIds.length) this.reconcileVisibility();
  }

  // ── (anchor) lock placed tiles during a move ───────────────────────────────
  private lockOthers(active: GridItemHTMLElement): void {
    if (!this.grid) return;
    const node = active.gridstackNode;
    if (!node) return; // never lock the tile being dragged
    for (const n of this.grid.engine.nodes) n.locked = n !== node;
  }
  private unlockAll(): void {
    if (!this.grid) return;
    for (const n of this.grid.engine.nodes) n.locked = false;
  }

  // ── (auto-scroll) exponential PAGE scroll while dragging/resizing ───────────
  // The window does NOT scroll — the page scrolls a `.main-content` container
  // (overflow:auto), so window.scrollBy is a no-op. Scroll the nearest scrollable
  // ANCESTOR instead, and measure the edge zone against ITS rect (not the
  // viewport), since it sits below the app header.
  private autoScrollRAF = 0;
  private ptrX = 0;
  private ptrY = 0;
  // No scrolling until the pointer has actually been seen. Before this was
  // tracked the zone test ran against (0, 0), which sits above the container's
  // top edge, so every drag began by hurling the page to the top.
  private ptrSeen = false;
  private scrollEl?: HTMLElement | null;
  private readonly trackPointer = (e: PointerEvent): void => {
    this.ptrX = e.clientX;
    this.ptrY = e.clientY;
    this.ptrSeen = true;
  };
  private findScrollParent(): HTMLElement {
    let node: HTMLElement | null = this.parentElement;
    while (node) {
      const oy = getComputedStyle(node).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentElement;
    }
    return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
  }
  private startAutoScroll(start?: Event): void {
    if (this.autoScrollRAF) return;
    this.scrollEl = this.findScrollParent();
    this.ptrSeen = false;
    // gridstack hands over the originating pointer event; seed from it so the
    // first frame already measures against where the hand actually is.
    if (start instanceof MouseEvent) this.trackPointer(start as PointerEvent);
    window.addEventListener('pointermove', this.trackPointer, true);
    const EDGE = 90; // px zone from each container edge where scroll begins
    const MAX = 26; // px/frame at the edge; grows past it
    const step = (): void => {
      const sc = this.scrollEl;
      if (sc && this.ptrSeen) {
        const r = sc.getBoundingClientRect();
        const fromTop = this.ptrY - r.top;
        const fromBottom = r.bottom - this.ptrY;
        let dy = 0;
        if (fromTop < EDGE) {
          const f = (EDGE - fromTop) / EDGE; // 0..1 in the zone, >1 past the edge
          dy = -Math.ceil(Math.min(f * f, 9) * MAX); // quadratic → accelerates with overshoot
        } else if (fromBottom < EDGE) {
          const f = (EDGE - fromBottom) / EDGE;
          dy = Math.ceil(Math.min(f * f, 9) * MAX);
        }
        if (dy) {
          const before = sc.scrollTop;
          sc.scrollTop += dy;
          // the container moved under a (possibly still) cursor → nudge gridstack
          // to recompute the dragged/resized tile against the new scroll.
          if (sc.scrollTop !== before) {
            document.dispatchEvent(
              new PointerEvent('pointermove', { clientX: this.ptrX, clientY: this.ptrY, buttons: 1, bubbles: true }),
            );
          }
        }
      }
      this.autoScrollRAF = requestAnimationFrame(step);
    };
    this.autoScrollRAF = requestAnimationFrame(step);
  }
  private stopAutoScroll(): void {
    if (this.autoScrollRAF) cancelAnimationFrame(this.autoScrollRAF);
    this.autoScrollRAF = 0;
    this.scrollEl = undefined;
    window.removeEventListener('pointermove', this.trackPointer, true);
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('panels') && this.grid) this.reconcilePanels();
    if (changed.has('hiddenIds') && this.grid) this.reconcileVisibility();
  }

  /**
   * Make the grid match `panels` after the first build: a panel that left the list takes its
   * item with it, a panel that arrived gets one (placed where `layout` says, else at its own
   * default), and a panel whose title changed says so in its bar. Without this the items were
   * built once, so a removed tile kept its empty frame and an installed one appeared only when
   * the whole grid was rebuilt by leaving the board and coming back.
   */
  private reconcilePanels(): void {
    const grid = this.grid;
    if (!grid) return;
    const built = new Map([...this.itemEls].map(([id, el]) => [id, el.querySelector('.pg-title')?.textContent ?? '']));
    const diff = diffPanels(built, this.panels);
    for (const id of diff.remove) {
      const el = this.itemEls.get(id);
      if (!el) continue;
      if (this.shown.has(id)) grid.removeWidget(el, true);
      else el.remove();
      this.itemEls.delete(id);
      this.shown.delete(id);
    }
    for (const { id, title } of diff.retitle) {
      const t = this.itemEls.get(id)?.querySelector('.pg-title');
      if (t) t.textContent = title;
    }
    const placed = new Map((this.layout ?? []).map((l) => [l.id, l]));
    for (const p of diff.add) {
      const el = this.buildItem(p, placed.get(p.id));
      this.itemEls.set(p.id, el);
      grid.el.appendChild(el);
      if (this.hiddenIds.includes(p.id)) {
        el.style.display = 'none';
      } else {
        grid.makeWidget(el);
        this.shown.add(p.id);
      }
    }
  }

  /** Show/hide items to match `hiddenIds` (gridstack add/remove; DOM kept). */
  private reconcileVisibility(): void {
    if (!this.grid) return;
    const hide = new Set(this.hiddenIds);
    for (const p of this.panels) {
      const el = this.itemEls.get(p.id);
      if (!el) continue;
      const wantShown = !hide.has(p.id);
      const isShown = this.shown.has(p.id);
      if (wantShown && !isShown) {
        el.style.display = '';
        this.grid.makeWidget(el); // re-add at its gs-x/gs-y
        this.shown.add(p.id);
      } else if (!wantShown && isShown) {
        this.grid.removeWidget(el, false); // remove from the engine, KEEP DOM + slot
        el.style.display = 'none';
        this.shown.delete(p.id);
      }
    }
  }

  private buildItem(p: PanelDef, pos?: PanelLayoutItem): HTMLElement {
    const item = document.createElement('div');
    item.className = 'grid-stack-item';
    const attr = (k: string, v: number | undefined) => {
      if (v !== undefined) item.setAttribute(k, String(v));
    };
    item.setAttribute('gs-id', p.id);
    item.addEventListener('mouseenter', () => item.classList.add('pg-hover'));
    item.addEventListener('mouseleave', () => item.classList.remove('pg-hover'));
    attr('gs-x', pos?.x ?? p.x);
    attr('gs-y', pos?.y ?? p.y);
    attr('gs-w', pos?.w ?? p.w);
    attr('gs-h', pos?.h ?? p.h);
    attr('gs-min-w', p.minW);
    attr('gs-min-h', p.minH);
    if (p.noResize || this.locked) item.setAttribute('gs-no-resize', 'true');
    if (p.noMove || this.locked) item.setAttribute('gs-no-move', 'true');
    if (this.locked) item.classList.add('pg-locked');

    const content = document.createElement('div');
    content.className = 'grid-stack-item-content';

    const body = document.createElement('div');
    body.className = 'pg-body';
    const bodySlot = document.createElement('slot');
    bodySlot.setAttribute('name', p.id);
    body.appendChild(bodySlot);

    const bar = document.createElement('div');
    bar.className = 'pg-bar';
    const drag = document.createElement('div');
    drag.className = 'pg-icon-btn pg-drag';
    drag.title = 'Move';
    drag.innerHTML = GRIP_SVG;
    const title = document.createElement('span');
    title.className = 'pg-title';
    title.textContent = p.title ?? '';
    const spacer = document.createElement('span');
    spacer.className = 'pg-spacer';
    const controls = document.createElement('div');
    controls.className = 'pg-controls';
    const ctrlSlot = document.createElement('slot');
    ctrlSlot.setAttribute('name', `${p.id}-controls`);
    const trash = document.createElement('button');
    trash.className = 'pg-icon-btn pg-trash';
    trash.title = 'Remove';
    trash.innerHTML = '<vitesse-icon name="trash-2" size="18"></vitesse-icon>';
    trash.addEventListener('click', () =>
      this.dispatchEvent(new CustomEvent('panel-remove', { detail: p.id, bubbles: true, composed: true })),
    );
    controls.append(ctrlSlot);
    if (!this.locked && !p.noRemove) controls.append(trash);
    bar.append(drag, title, spacer, controls);

    content.append(body, bar);
    item.appendChild(content);
    return item;
  }

  private emitTimer?: ReturnType<typeof setTimeout>;
  private scheduleEmit(): void {
    clearTimeout(this.emitTimer);
    this.emitTimer = setTimeout(() => this.emitLayout(), 150);
  }

  private emitLayout(): void {
    if (!this.grid) return;
    const saved = this.grid.save(false) as GridStackNode[];
    const layout: PanelLayoutItem[] = saved.map((n) => ({
      id: String(n.id ?? ''),
      x: n.x ?? 0,
      y: n.y ?? 0,
      w: n.w ?? 1,
      h: n.h ?? 1,
    }));
    this.dispatchEvent(new CustomEvent('layout-change', { detail: layout, bubbles: true, composed: true }));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this.emitTimer);
    this.stopAutoScroll();
    this.grid?.destroy(false); // detach gridstack; Lit removes the shadow DOM
    this.grid = undefined;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-panel-grid': VitessePanelGrid;
  }
}
